import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Build a professional aggregated license invoice PDF.
 * The "invoice" rolls up the base license row + every grant batch into a single
 * document with line items, subtotals, and payment terms.
 *
 * @param {object}  params
 * @param {object}  params.company         - { name, email, phone, city, country, website }
 * @param {object}  params.licenseInfo     - { base, baseUnitAmountCents, currency, expirationDate, paymentDate, grants:[...] }
 * @param {string}  [params.invoiceNumber] - Override the auto-generated invoice number
 * @param {string}  [params.logoDataUrl]   - Pre-loaded data URL of the JobastiIQ logo (optional)
 */
export function downloadLicenseInvoice({ company, licenseInfo, invoiceNumber, logoDataUrl } = {}) {
  if (!company || !licenseInfo) return;
  const currency = licenseInfo.currency || 'EUR';
  const fmtMoney = (cents) => {
    if (!cents || cents === 0) return '—';
    try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(cents / 100); }
    catch { return `${(cents / 100).toFixed(2)} ${currency}`; }
  };
  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
    catch { return iso; }
  };

  const today = new Date();
  const fallbackNo = `INV-JBS-${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, '0')}-${String(company.id || '').slice(0, 6).toUpperCase()}`;
  const invNo = invoiceNumber || fallbackNo;

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 48;

  // ── Header band ──────────────────────────────────────────────────────────
  doc.setFillColor(245, 248, 252);
  doc.rect(0, 0, pageW, 110, 'F');

  if (logoDataUrl) {
    try { doc.addImage(logoDataUrl, 'PNG', margin, 32, 110, 36, undefined, 'FAST'); }
    catch { /* ignore */ }
  } else {
    doc.setFont('helvetica', 'bold').setFontSize(20).setTextColor(15, 23, 42);
    doc.text('JobastiIQ', margin, 58);
  }

  doc.setFont('helvetica', 'bold').setFontSize(22).setTextColor(15, 23, 42);
  doc.text('INVOICE', pageW - margin, 60, { align: 'right' });
  doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(100, 116, 139);
  doc.text(invNo, pageW - margin, 78, { align: 'right' });
  doc.text(`Issued ${today.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}`, pageW - margin, 92, { align: 'right' });

  // ── Meta blocks ──────────────────────────────────────────────────────────
  let y = 150;
  doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor(100, 116, 139);
  doc.text('FROM', margin, y);
  doc.text('BILL TO', pageW / 2, y);

  doc.setFont('helvetica', 'bold').setFontSize(11).setTextColor(15, 23, 42);
  doc.text('JobastiIQ GmbH', margin, y + 18);
  doc.text(company.name || '—', pageW / 2, y + 18);

  doc.setFont('helvetica', 'normal').setFontSize(9.5).setTextColor(71, 85, 105);
  const fromLines = [
    'Berlin, Germany',
    'billing@jobasti.com',
    'VAT-ID: [your VAT id]',
    'HRB: [your registration]',
  ];
  fromLines.forEach((line, i) => doc.text(line, margin, y + 36 + i * 14));

  const toLines = [
    company.city ? `${company.city}${company.country ? ', ' + company.country : ''}` : (company.country || '—'),
    company.email || '',
    company.phone || '',
    company.website || '',
  ].filter(Boolean);
  toLines.forEach((line, i) => doc.text(line, pageW / 2, y + 36 + i * 14));

  // ── Line items ───────────────────────────────────────────────────────────
  const rows = [];
  const base = licenseInfo.base || 0;
  const baseSubtotal = licenseInfo.baseTotalAmountCents || 0;
  rows.push([
    'Base license plan',
    String(base),
    '—',
    fmtMoney(baseSubtotal),
    fmtDate(licenseInfo.expirationDate),
    fmtDate(licenseInfo.paymentDate),
  ]);

  let grantsTotal = 0;
  (licenseInfo.grants || []).forEach((g, idx) => {
    const subtotal = g.totalAmountCents || 0;
    grantsTotal += subtotal;
    rows.push([
      `Additional licenses${g.note ? ` — ${g.note}` : ` (batch ${idx + 1})`}`,
      String(g.seats || 0),
      '—',
      fmtMoney(subtotal),
      fmtDate(g.expirationDate),
      fmtDate(g.paymentDate),
    ]);
  });

  const grandTotal = baseSubtotal + grantsTotal;

  autoTable(doc, {
    startY: y + 110,
    head: [['Description', 'Licenses', 'Unit price', 'Subtotal', 'Expires', 'Payment date']],
    body: rows,
    margin: { left: margin, right: margin },
    styles: { fontSize: 9.5, cellPadding: 8, textColor: [30, 41, 59], lineColor: [226, 232, 240] },
    headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 9 },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: {
      1: { halign: 'right' },
      2: { halign: 'right' },
      3: { halign: 'right', fontStyle: 'bold' },
    },
  });

  // ── Totals block ─────────────────────────────────────────────────────────
  const afterTableY = doc.lastAutoTable.finalY + 18;
  const totalsX = pageW - margin - 220;
  doc.setDrawColor(226, 232, 240).setLineWidth(0.5);
  doc.line(totalsX, afterTableY, pageW - margin, afterTableY);

  doc.setFont('helvetica', 'normal').setFontSize(10).setTextColor(71, 85, 105);
  doc.text('Subtotal', totalsX, afterTableY + 20);
  doc.text(fmtMoney(grandTotal), pageW - margin, afterTableY + 20, { align: 'right' });

  doc.text('VAT (0%)', totalsX, afterTableY + 38);
  doc.text(fmtMoney(0), pageW - margin, afterTableY + 38, { align: 'right' });

  doc.setDrawColor(15, 23, 42).setLineWidth(1);
  doc.line(totalsX, afterTableY + 50, pageW - margin, afterTableY + 50);
  doc.setFont('helvetica', 'bold').setFontSize(13).setTextColor(15, 23, 42);
  doc.text('Total due', totalsX, afterTableY + 70);
  doc.text(fmtMoney(grandTotal), pageW - margin, afterTableY + 70, { align: 'right' });

  // ── Payment terms ────────────────────────────────────────────────────────
  const termsY = afterTableY + 110;
  doc.setFont('helvetica', 'bold').setFontSize(10).setTextColor(15, 23, 42);
  doc.text('Payment terms', margin, termsY);
  doc.setFont('helvetica', 'normal').setFontSize(9).setTextColor(71, 85, 105);
  const terms = [
    'Payment due within 14 days of the issue date unless otherwise agreed.',
    'Bank transfer details and reference will be communicated by JobastiIQ billing.',
    'Please reference invoice number ' + invNo + ' on the transfer.',
    'Questions? Contact billing@jobasti.com',
  ];
  terms.forEach((t, i) => doc.text(t, margin, termsY + 18 + i * 13));

  // ── Footer ───────────────────────────────────────────────────────────────
  const footerY = doc.internal.pageSize.getHeight() - 36;
  doc.setDrawColor(226, 232, 240).setLineWidth(0.5);
  doc.line(margin, footerY - 12, pageW - margin, footerY - 12);
  doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(148, 163, 184);
  doc.text('JobastiIQ GmbH  ·  VAT-ID: [your VAT id]  ·  HRB: [your registration]  ·  billing@jobasti.com', pageW / 2, footerY, { align: 'center' });

  doc.save(`${invNo}.pdf`);
}

/** Load /jobasti-logo.png as a dataURL for embedding into the PDF. */
export async function loadJobastiLogoDataUrl() {
  try {
    const res = await fetch('/jobasti-logo.png');
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch { return null; }
}
